import { ActivatedRoute } from '@angular/router';
import { ItemService } from '../services/item.service';
import { Component, OnDestroy } from "@angular/core";
import { FormGroup, FormBuilder, FormArray } from "@angular/forms";
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { StreamService, TenantService } from '../services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntil, flatMap, filter, first, tap, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { HasChanges } from '../services/HasChanges';
@Component({
    styles: [`
        .mat-chip {
            line-height: 2.8em;
        }
        .mat-chip:not(.mat-basic-chip) {
            color: white;
            background-color: #3F51B5;
        }
    `],
    template: ` 
        <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
            <spinner syle="padding-top: 100px;"></spinner>
        </div>
        <mat-card *ngIf="!noTouching && wrongRev && !wrongType" style="margin: 15px;">
            <mat-card-content fxLayout="column">
                <h3>This filter cannot be edited in this editor, please use the <a [attr.href]="'/topics/' + streamId + '/edit/filters'">admin editor</a>.</h3>
            </mat-card-content>    
        </mat-card>
        <mat-card *ngIf="!noTouching && wrongType" style="margin: 15px;">
            <mat-card-content fxLayout="column">
                <h3>Sources cannot be filtered directly.</h3>
            </mat-card-content>    
        </mat-card>
        <mat-card *ngIf="!noTouching && !wrongRev && !wrongType" [formGroup]="form" style="margin: 15px;">
            <mat-card-content fxLayout="column">
                <div fxFlex="100" fxLayoutAlign="end center" *ngIf="enableAdvancedFilters">
                    <mat-checkbox formControlName="isAdvanced" matTooltip="Allows entry of Boolean queries.">Advanced</mat-checkbox>
                    <div style="height: 20px; width: 40px;">
                        <help-link *ngIf="this.form.get('isAdvanced').value"
                                   style="padding-left: 10px;" 
                                   articleSlug="115050-search-and-filter-syntax"></help-link>
                    </div>
                </div>
                <div fxLayout="row" *ngIf="!this.form.get('isAdvanced').value">
                    <mat-form-field style="width: 50%">
                        <input matInput placeholder="Text" formControlName="basicFilterText">
                    </mat-form-field>
                    <mat-form-field style="min-width: 400px; padding-left:20px;">
                        <mat-select placeholder="Match Type" formControlName="matchType" fxFlex>
                            <mat-option value="IncludeAllKeywords">Include All Keywords</mat-option>
                            <mat-option value="IncludeAnyKeywords">Include Any Keywords</mat-option>
                            <mat-option value="MatchExactPhrase">Match Exact Phrase</mat-option>
                        </mat-select>
                    </mat-form-field>
                    <div fxFlex fxFlexOffset="5">
                        <mat-spinner *ngIf="noTouchingCount" diameter="15"></mat-spinner>
                        <h3 *ngIf="!noTouchingCount">{{totalItems}} Total Items</h3>
                    </div>
                    <span [matTooltip]="hasEmptyGroups() ? 'Remove empty tag group before adding another' : 'Add another tag group'">
                        <button mat-raised-button 
                                color="primary" 
                                (click)="addTagGroup()" 
                                [disabled]="hasEmptyGroups()">Add Tag Group</button>
                    </span>
                </div>
                <basic-filter-editor *ngIf="!this.form.get('isAdvanced').value" 
                                     [basicFilterStructures]="form.get('basicFilterStructures')"
                                     [availableTags]="availableTags"
                                     [keyTags]="keyTags"
                                     [docCount]="docCount"
                                     [disabled]="noTouchingCount"
                                     (tagAdded)="fetchTotalItemsAndTagCounts('tag Added')"
                                     (tagRemoved)="fetchTotalItemsAndTagCounts('tag Removed')"
                                     (tagGroupRemoved)="fetchTotalItemsAndTagCounts('tag Group Removed')"></basic-filter-editor>
                <div *ngIf="this.form.get('isAdvanced').value">
                    <mat-form-field fxFlex>
                        <textarea matInput formControlName="advancedFilterTerm" fxFlex="100" placeholder="Advanced Filter "></textarea>
                    </mat-form-field>
                    <div fxFlex fxFlexOffset="5">
                        <mat-spinner *ngIf="noTouchingCount" diameter="15"></mat-spinner>
                        <h3 *ngIf="!noTouchingCount">{{totalItems}} Total Items</h3>
                    </div>
                </div>
                <div>
                    <mat-form-field fxFlex="250px">
                        <input matInput placeholder="Only articles published in the last" formControlName="publishedWithinDays">
                        <span matSuffix>days</span>
                    </mat-form-field>
                </div>
            </mat-card-content>
            <mat-card-actions>
                <button mat-raised-button color="primary" (click)="saveFilter()">Apply</button>
            </mat-card-actions>
        </mat-card>
    `
})
export class EditTopicContentFiltersTabComponent implements OnDestroy, HasChanges {
    noTouching: boolean = false;
    noTouchingCount: boolean = false;
    enableAdvancedFilters: boolean;
    form: FormGroup;
    private cleanupTime = new Subject<void>();
    streamId: any;
    availableTags = [];
    keyTags = [];
    docCount: number;
    stream: any;
    wrongRev: boolean;
    totalItems: any;
    tagMap: any;
    originalValue: any;
    wrongType: boolean;

    constructor(private fb: FormBuilder, 
                route: ActivatedRoute, 
                private snackBar: MatSnackBar, 
                private itemService: ItemService, 
                private streamService: StreamService,
                tenantService: TenantService) {
        
        tenantService.getUnauthTenantInfo().pipe(first())
        .subscribe(tenant => { this.enableAdvancedFilters = tenant.settings.enableAdvancedFilters === 'true'; });

        this.form = fb.group({
            isAdvanced: [false],
            basicFilterText: [''],
            matchType: ['IncludeAllKeywords'],
            basicFilterStructures: fb.array([]),
            advancedFilterTerm: [''],
            publishedWithinDays: ['']
        });

        this.form.get('basicFilterText').valueChanges
        .pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            takeUntil(this.cleanupTime)
        )
        .subscribe(() => this.fetchAvailableTags());

        this.form.get('matchType').valueChanges
        .pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            takeUntil(this.cleanupTime)
        )
        .subscribe(() => this.fetchAvailableTags());

        this.form.get('advancedFilterTerm').valueChanges
        .pipe(
            takeUntil(this.cleanupTime),
            debounceTime(1000),
            distinctUntilChanged()
        )
        .subscribe(() => this.fetchAvailableTags());

        route.parent.parent.params
        .pipe(
            takeUntil(this.cleanupTime),
            tap(() => this.noTouching = true),
            tap((params: { topicId }) => this.streamId = params.topicId),
            flatMap((params: { topicId }) => this.populate())
        )
        .subscribe(() => this.originalValue = _.cloneDeep(this.form.value));

        this.form.get('isAdvanced').valueChanges
        .pipe(
            takeUntil(this.cleanupTime),
            filter(checked => checked),
        )
        .subscribe(() => this.form.get('advancedFilterTerm').setValue(this.streamService.basicFilterTermFromFilter(this.form.value), { emitEvent: false }));
    }

    hasEmptyGroups(): boolean {
        return  this.form.value.basicFilterStructures.length > 0 && !this.form.value.basicFilterStructures.filter(basicStruct => basicStruct.values.length).length;
    }

    private doSearch(): Observable<any> {
        const search = this.form.get('isAdvanced').value ? this.form.get('advancedFilterTerm').value : this.streamService.basicFilterTermFromFilter(this.form.value);
        const publishedWithinDays = this.form.get('publishedWithinDays').value || undefined;
        return this.itemService.searchItemsWithSettings(
            { 
                search: search.trim().length ? search : undefined,
                searchConfiguration: {filterStructures: this.form.value.basicFilterStructures },
                publishedWithinDays: publishedWithinDays 
            }, 
            {  
                includeAggregations: true, streamId: this.stream.id, ignoreTopLevelFilter: true, page: 0, rows: 1
            }
        );
    }

    fetchTotalItemsAndTagCounts(source): void {
        this.noTouchingCount = true;
        this.doSearch().pipe(
            tap(() => this.noTouchingCount = false),
            tap(() => this.noTouching = false),
            tap(searchRes => this.totalItems = searchRes.totalElements),
        )
        .subscribe(searchRes => {
            const updatedTags = _(searchRes.aggregations.tagTypes.type.buckets)
                .map(bucket =>  _.map(bucket.tagText.buckets, tag => _.extend({}, tag, { type: bucket.key, tagText: tag.key })))
                .flatten()
                .keyBy(item => item.type + item.key)
                .value();
            this.availableTags.forEach(tag => {
                const updated = updatedTags[tag.type + tag.key];
                tag.doc_count = updated ? updated.doc_count : 0;
            });
        });
    }

    fetchAvailableTags(): void {
        this.noTouchingCount = true;
        this.doSearch().pipe(first()).subscribe(searchRes => {
            this.noTouchingCount = false;
            this.totalItems = searchRes.totalElements;

            this.keyTags = [];
            this.docCount = searchRes.aggregations ? searchRes.aggregations.tagTypes.doc_count : 0;
            this.availableTags = _(searchRes.aggregations ? searchRes.aggregations.tagTypes.type.buckets : null)
            .map(bucket => {
                this.keyTags.push(bucket.key);
                return _.map(bucket.tagText.buckets, tag => _.extend({}, tag, { key: tag.key || tag.tagText, type: bucket.key }))
            })
            .flatten()
            .sortBy(['doc_count'])
            .reverse()
            .value();

            this.tagMap = _.keyBy(this.availableTags, tag => `${tag.key}_${tag.type}`);

            this.noTouching = false;
        });
    }

    populate(): Observable<any> {
        return this.streamService.getStream(this.streamId)
        .pipe(
             tap(stream => this.wrongType = stream.type !== 'COLLECTION'),
             tap((stream) => {
                if (!stream.filter) {
                    stream.filter = {
                        version: '2',
                        isBasic: true, 
                        filterTerm: '',
                        basicFilterText: '',
                        basicFilterStructures: []
                    }
                }

                this.wrongRev = !stream.filter || stream.filter.version !== '2';
                if (!this.wrongRev) {           
                    this.stream = stream;
                    this.form.get('basicFilterText').setValue(this.stream.filter ? this.stream.filter.basicFilterText : '', { emitEvent: false });
                    this.form.get('matchType').setValue(this.stream.filter && this.stream.filter.matchType ? this.stream.filter.matchType : 'IncludeAllKeywords', { emitEvent: false });
                    this.form.get('isAdvanced').setValue(this.stream.filter ? !this.stream.filter.isBasic : false, { emitEvent: false });
                    
                    let basicFilterStructures = <FormArray> this.form.get('basicFilterStructures');
                    while (basicFilterStructures.length !== 0) {
                        basicFilterStructures.removeAt(0)
                    }

                    _.forEach(this.stream.filter ? this.stream.filter.basicFilterStructures : [], filterStructure => {
                        basicFilterStructures.push(this.fb.group({
                            condition: this.fb.control(filterStructure.condition),
                            type: this.fb.control(filterStructure.type),
                            values: this.fb.array(_.map(filterStructure.values, fsVal =>  {
                                return this.fb.group({
                                    key: this.fb.control(fsVal.key || fsVal.tagText),
                                    tagText: this.fb.control(fsVal.tagText),
                                    type: this.fb.control(fsVal.type),
                                    doc_count: this.fb.control(fsVal.doc_count),
                                })
                            }
                            ))
                        }));
                    })

                    this.form.get('advancedFilterTerm').setValue(this.stream.filter ? this.stream.filter.advancedFilterTerm : '', { emitEvent: false }); 
                    this.form.get('publishedWithinDays').setValue(this.stream.filter ? this.stream.filter.publishedWithinDays : null, { emitEvent: false }); 
                    this.fetchAvailableTags();
                }
                this.noTouching = false;
            })
        );
    }

    addTagGroup(): void {
        const basicFilterStructures = <FormArray> this.form.get('basicFilterStructures');
        basicFilterStructures.push(this.fb.group({
            condition: this.fb.control('CONTAINS'),
            type: this.fb.control('TAG'),
            values: this.fb.array([])
        }));
    }

    filterIsEmpty(filter): boolean {
        return !filter || ((!filter.filterTerm || filter.filterTerm.trim().length === 0) && !filter.publishedWithinDays && (!filter.basicFilterStructures || filter.basicFilterStructures.length === 0));
    }

    saveFilter(): void {
        this.noTouching = true;
        const filter = this.stream.filter || { };
        filter.matchType = this.form.get('matchType').value;
        filter.basicFilterText = this.cleanQuotes(this.form.get('basicFilterText').value);
        filter.basicFilterStructures = this.form.get('basicFilterStructures').value.filter(basicStruct => basicStruct.values.length);
        filter.publishedWithinDays = this.form.get('publishedWithinDays').value;
        const advancedFilterTerm = this.cleanQuotes(this.form.get('advancedFilterTerm').value);
        filter.advancedFilterTerm = _.isEmpty(advancedFilterTerm) ? undefined : this.cleanQuotes(this.form.get('advancedFilterTerm').value);
        filter.isBasic = !this.form.get('isAdvanced').value;
        filter.filterTerm = filter.isBasic ? this.streamService.basicFilterTermFromFilter(this.form.value) : filter.advancedFilterTerm;

        if (this.filterIsEmpty(filter)) {
            this.streamService.deleteFilter(this.stream.id, filter.id)
            .pipe(
                flatMap(() => this.populate()),
                tap(() => this.originalValue = _.cloneDeep(this.form.value)),
                tap(() => this.noTouching = false),
                tap(() => this.snackBar.open(`Filter removed.`, 'OK', { duration: 2000 }))
            )
            .subscribe(() => this.streamService.streamUpdated(this.stream));
        } else {
            if(filter.basicFilterText?.length === 0) {
                filter.basicFilterText = undefined;
            }
            if(filter.filterTerm?.length === 0) {
                filter.filterTerm = undefined;
            }

            this.streamService.postFilter(this.stream.id, filter)
            .pipe(
                flatMap(() => this.populate()),
                tap(() => this.originalValue = _.cloneDeep(this.form.value)),
                tap(() => this.noTouching = false),
                tap(() => this.snackBar.open(`Filter changes saved.`, 'OK', { duration: 2000 }))
            )
            .subscribe(() => this.streamService.streamUpdated(this.stream));
        }
    }

    cleanQuotes = target => target ? target.replace(/[\u2018\u2019]/g, '\'').replace(/[\u201C\u201D]/g, '"') : target;

    hasChanges(): boolean {
        return !_.isEqual(this.form.value, this.originalValue);
    }
    existingChangesMessage(): string {
        return `The changes to the filter for ${this.stream.title} will be lost unless applied. Continue?`;
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
    }
}
